import { useMemo, useState, type ReactNode } from "react";
import { Badge, EmptyState, GridTable, Pager, SearchField, Tag, WidgetCard, ds, type GridColumn } from "@/shared/components/ds";
import { humanize, show } from "../format";
import type {
    DataSpecAttribute, LaunchDataSpec, LaunchDetail, LaunchEvent, LaunchMain, LaunchOptions, LaunchPublisher,
    LaunchReporting, LaunchServing, UploadHistoryRow, ValueSet,
} from "../models/launchDetail";
import pageStyles from "../opLaunchPages.module.css";
import { ExpandableGrid, Fields, Mini, YesNo, pick, type FieldSpec } from "./DetailParts";
import styles from "./launchDetail.module.css";

/*
    The read-only launch tabs, in OP's order minus Data Manipulation (MPN-2229). Each takes
    its group of the detail payload and renders widget cards: labelled rows for scalars,
    grids for lists. A group the warehouse has nothing for collapses to "No data for this launch".
*/

const ext = (url?: string | null) => url
    ? <a href={url} target="_blank" rel="noreferrer" className={ds.link}>{url}</a>
    : <span className={ds.muted}>—</span>;
const dash = <span className={ds.muted}>—</span>;
const text = (v: unknown) => (v === null || v === undefined || v === "" ? dash : show(v));
const tags = (list?: (string | null | undefined)[] | null) =>
    list?.length ? <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{list.map((t, i) => <Tag key={i}>{show(t)}</Tag>)}</span> : dash;

// --- Main ------------------------------------------------------------------------------

const MAIN_TABLES: (keyof LaunchMain)[] = ["engagement_team", "key_dates", "recommended_actions", "business_estimates"];
const INFO: (keyof LaunchMain | [keyof LaunchMain, string] | [keyof LaunchMain, string, { full?: boolean }])[] = [
    ["name", "Name"], ["affiliate_name", "Affiliate"], ["customer_name", "Customer"], ["use_cases", "Use Cases"], ["census_deal", "Census Deal"],
    ["purchase_order_number", "Purchase Order Number"], ["goal", "Goal", { full: true }], ["additional_information", "Additional Information", { full: true }],
];
const DATES: [keyof LaunchMain, string][] = [
    ["start_date", "Start Date"], ["end_date", "End Date"], ["launch_duration_type", "Launch Duration Type"], ["duration_in_days", "Duration In Days"],
    ["management_start_date", "Management Start Date"], ["management_end_date", "Management End Date"], ["is_active", "Is Active"],
    ["in_definition", "In Definition"], ["auto_set_start_date_on_first_upload", "Auto Set Start Date On First Upload"], ["timezone", "Timezone"],
];
const RECORD: [keyof LaunchMain, string][] = [["id", "ID"], ["datetime_created", "Created"], ["created_by", "Created By"], ["datetime_modified", "Modified"]];
const BUDGET: [keyof LaunchMain, string][] = [
    ["invoice_reference", "Invoice Reference"], ["revenue_type", "Revenue Type"], ["monthly_budget_type", "Monthly Budget Type"], ["monthly_budget", "Monthly Budget"],
    ["revenue_cap_type", "Revenue Cap Type"], ["revenue_cap", "Revenue Cap"], ["account_for_serving_groups", "Account For Serving Groups"], ["total_budget", "Total Budget"],
];
const ROLES: Record<string, string> = { EM: "Engagement Manager", OM: "Ops Manager", DA: "Data Analyst", SL: "Sales Lead" };

function MainTab({ main }: { main?: LaunchMain | null }) {
    if (!main) return <WidgetCard title="Launch Info" isEmpty />;
    const placed = new Set<string>([...MAIN_TABLES, ...INFO, ...DATES, ...RECORD, ...BUDGET].map((k) => String(Array.isArray(k) ? k[0] : k)));
    // Anything the warehouse sends that this page did not plan for still shows, humanized.
    const extra: FieldSpec[] = Object.entries(main).filter(([k]) => !placed.has(k)).map(([k, v]) => [humanize(k), v]);
    const team = main.engagement_team ?? [];
    const keyDates = main.key_dates ?? [];
    const actions = main.recommended_actions ?? [];
    const estimates = main.business_estimates ?? [];
    return (
        <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div className={pageStyles.widgetsWide}>
                <div className={pageStyles.column}>
                    <WidgetCard title="Launch Info"><Fields fields={[...pick(main, INFO), ...extra]} /></WidgetCard>
                </div>
                <div className={pageStyles.column}>
                    <WidgetCard title="Dates"><Fields fields={pick(main, DATES)} /></WidgetCard>
                    <WidgetCard title="Record"><Fields fields={pick(main, RECORD)} /></WidgetCard>
                </div>
                <div className={pageStyles.column}>
                    <WidgetCard title="Budget and Revenue"><Fields fields={pick(main, BUDGET)} /></WidgetCard>
                </div>
            </div>
            <WidgetCard title="Engagement Team" count={team.length} isEmpty={!team.length}>
                <GridTable columns={[{ label: "Role", track: "90px" }, { label: "Name", track: "minmax(160px, 1fr)", tone: "strong" }, { label: "Email", track: "minmax(220px, 2fr)" }]}
                    rows={team.map((m) => [
                        <span className={styles.role} title={ROLES[m.role ?? ""] ?? m.role ?? undefined}>{show(m.role)}</span>,
                        text(m.name), text(m.email),
                    ])} />
            </WidgetCard>
            <div className={pageStyles.widgets}>
                <WidgetCard title="Key Dates" count={keyDates.length} isEmpty={!keyDates.length}>
                    <GridTable columns={[{ label: "Type", tone: "strong" }, { label: "Date", track: "110px" }, { label: "Label", track: "1.4fr" }]}
                        rows={keyDates.map((k) => [text(k.type), text(k.date), text(k.label)])} />
                </WidgetCard>
                <WidgetCard title="Recommended Actions" count={actions.length} isEmpty={!actions.length}>
                    <GridTable columns={[{ label: "Action", tone: "strong" }, { label: "Status" }]}
                        rows={actions.map((a) => [text(a.action), text(a.status)])} />
                </WidgetCard>
                <WidgetCard title="Business Estimates" count={estimates.length} isEmpty={!estimates.length}>
                    <GridTable columns={[{ label: "Launch Attribute", tone: "mono" }, { label: "Target Attribute Values" }, { label: "Amount Paid", align: "right" }]}
                        rows={estimates.map((e) => [text(e.launch_attribute), text(e.target_attribute_values), text(e.amount_paid)])} />
                </WidgetCard>
            </div>
        </section>
    );
}

// --- Options ---------------------------------------------------------------------------

function OptionsTab({ options }: { options?: LaunchOptions | null }) {
    const entries = Object.entries(options ?? {});
    const fields: FieldSpec[] = entries.map(([k, v]) => [humanize(k), v]);
    const half = Math.ceil(fields.length / 2);
    return (
        <WidgetCard title="Options" count={entries.length} isEmpty={!entries.length}>
            <div className={styles.twoCol}>
                <Fields fields={fields.slice(0, half)} />
                <Fields fields={fields.slice(half)} />
            </div>
        </WidgetCard>
    );
}

// --- Publishers ------------------------------------------------------------------------

function PublishersTab({ publishers }: { publishers?: LaunchPublisher[] | null }) {
    const rows = publishers ?? [];
    return (
        <WidgetCard title="Publishers" count={rows.length} isEmpty={!rows.length}>
            <GridTable columns={[{ label: "Platform", track: "minmax(140px, 1fr)" }, { label: "Audience Type", track: "minmax(200px, 2fr)" }]}
                rows={rows.map((p) => [text(p.publisher_platform), p.audience_type ? <Tag>{p.audience_type}</Tag> : dash])} />
        </WidgetCard>
    );
}

// --- Data Spec -------------------------------------------------------------------------

const VALUE_SET_COLUMNS: GridColumn[] = [{ label: "Value", tone: "mono" }, { label: "Description", track: "1.4fr" }, { label: "Attribute Set" }];

function ValueSets({ sets }: { sets: ValueSet[] }) {
    return (
        <div>
            <div className={`${styles.miniLabel} ${styles.nestedLabel}`}>Value Sets · {sets.length}</div>
            <div className={styles.nested}>
                <GridTable columns={VALUE_SET_COLUMNS} rows={sets.map((v) => [text(v.value), text(v.description), text(v.attribute_set)])} />
            </div>
        </div>
    );
}

const ATTR_COLUMNS: GridColumn[] = [
    { label: "#", track: "44px", tone: "id" }, { label: "Name", track: "minmax(160px, 1fr)", tone: "mono" }, { label: "Description", track: "minmax(260px, 2fr)" },
    { label: "Sample Value", track: "minmax(170px, 1fr)", tone: "mono" }, { label: "Required", track: "84px" }, { label: "Values Change", track: "104px" },
    { label: "Is Serving Distinction", track: "124px" },
];

function attributeDetail(a: DataSpecAttribute) {
    return (
        <>
            <div className={styles.mini}>
                <Mini label="Attribute" value={a.attribute} />
                <Mini label="Reason" value={a.reason} />
                <Mini label="Max Length" value={a.max_length} />
                <Mini label="Validator Range" value={a.validator_range} />
                <Mini label="Validator Items Count" value={a.validator_items_count} />
                <Mini label="Regex" value={a.validator_regular_expression} mono />
                <Mini label="Persist First Value" value={a.persist_first_value} />
                <Mini label="Percent Required Values" value={a.percent_required_values} />
                <Mini label="Is Virtual" value={a.is_virtual} />
            </div>
            {!!a.value_sets?.length && <ValueSets sets={a.value_sets} />}
        </>
    );
}

function DataSpecTab({ spec }: { spec?: LaunchDataSpec | null }) {
    const [q, setQ] = useState("");
    const attrs = spec?.attributes ?? [];
    const sys = spec?.system_attributes ?? [];
    const needle = q.trim().toLowerCase();
    const shown = attrs
        .map((a, i) => ({ a, n: i + 1 }))
        .filter(({ a }) => !needle || (a.name ?? "").toLowerCase().includes(needle) || (a.description ?? "").toLowerCase().includes(needle));
    return (
        <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <WidgetCard title="Attributes" count={attrs.length} isEmpty={!attrs.length}>
                <div className={styles.toolbar}>
                    <SearchField value={q} onChange={setQ} placeholder="Search attributes, e.g. is_registered_F26" width={300} label="Search attributes" />
                    <span className={styles.hint}>{needle ? `${shown.length} of ${attrs.length} attributes` : "Expand a row for validators and value sets"}</span>
                </div>
                <ExpandableGrid columns={ATTR_COLUMNS} minWidth={1040}
                    empty={needle ? `No attributes match "${q.trim()}"` : undefined}
                    rows={shown.map(({ a, n }) => ({
                        key: a.name ?? n,
                        cells: [
                            n, text(a.name),
                            <span className={styles.clamp2} title={a.description ?? undefined}>{text(a.description)}</span>,
                            <span className={styles.ellipsis} title={a.sample_value ?? undefined}>{text(a.sample_value)}</span>,
                            text(a.required_form),
                            <YesNo value={a.values_changes_for_constituent_instance} />,
                            <YesNo value={a.is_serving_distinction} />,
                        ],
                        detail: attributeDetail(a),
                    }))} />
            </WidgetCard>
            <WidgetCard title="System Attributes" count={sys.length} isEmpty={!sys.length}>
                <ExpandableGrid flush columns={[{ label: "Name", tone: "strong" }, { label: "System Attribute Name", tone: "mono" }]}
                    rows={sys.map((s, i) => ({
                        key: s.name ?? i,
                        cells: [text(s.name), text(s.system_attribute_name)],
                        detail: s.value_sets?.length ? <ValueSets sets={s.value_sets} /> : undefined,
                    }))} />
            </WidgetCard>
        </section>
    );
}

// --- Upload History --------------------------------------------------------------------

const num = (v: unknown) => (typeof v === "number" ? v.toLocaleString("en-US") : show(v));

function UploadHistoryTab({ history }: { history?: UploadHistoryRow[] | null }) {
    const [page, setPage] = useState(1);
    const [per, setPer] = useState(10);
    const rows = useMemo(() => [...(history ?? [])].sort((a, b) => ((a.as_of ?? "") < (b.as_of ?? "") ? 1 : -1)), [history]);
    const statKeys = useMemo(() => {
        const keys: string[] = [];
        rows.forEach((r) => Object.keys(r.statistics ?? {}).forEach((k) => { if (!keys.includes(k)) keys.push(k); }));
        return keys;
    }, [rows]);
    const totalKey = statKeys.find((k) => /total/i.test(k));
    const pages = Math.max(1, Math.ceil(rows.length / per));
    const current = Math.min(page, pages);
    const from = (current - 1) * per;
    const shown = rows.slice(from, from + per);
    const columns: GridColumn[] = [
        { label: "As Of", track: "104px", tone: "strong", nowrap: true }, { label: "Status", track: "112px" },
        { label: "Uploaded", track: "minmax(190px, 1.3fr)", nowrap: true }, { label: "Agent Type", track: "120px", nowrap: true },
        ...statKeys.map((k) => ({ label: humanize(k), track: "minmax(80px, 1fr)", align: "right" as const })),
    ];
    const statCell = (v: unknown) => <span className={v === 0 || v === "0" ? styles.zero : undefined}>{num(v)}</span>;
    return (
        <WidgetCard title="Upload History" count={rows.length} isEmpty={!rows.length}>
            <ExpandableGrid flush columns={columns} minWidth={Math.max(760, 540 + statKeys.length * 90)}
                rows={shown.map((u, i) => {
                    const stats = u.statistics ?? {};
                    const total = totalKey ? Number(stats[totalKey]) : NaN;
                    return {
                        key: `${u.as_of ?? ""}-${from + i}`,
                        cells: [
                            text(u.as_of),
                            u.status_label ? <Badge tone={u.status === true || /success|complete|ok/i.test(u.status_label) ? "success" : "neutral"} dot>{u.status_label}</Badge> : dash,
                            text(u.datetime_uploaded), text(u.agent_type),
                            ...statKeys.map((k) => statCell(stats[k])),
                        ],
                        detail: (
                            <>
                                <div className={styles.tiles}>
                                    {statKeys.map((k) => (
                                        <div key={k} className={styles.tile}>
                                            <div className={styles.miniLabel}>{humanize(k)}</div>
                                            <div className={styles.tileValue}>
                                                <span className={styles.tileNumber}>{num(stats[k])}</span>
                                                {totalKey && k !== totalKey && total > 0 && typeof stats[k] === "number" && (
                                                    <span className={styles.tilePct}>{((stats[k] as number / total) * 100).toFixed(1)}% of total</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <div className={styles.miniLabel}>Statistics</div>
                                    <div className={styles.raw}>{JSON.stringify(stats)}</div>
                                </div>
                            </>
                        ),
                    };
                })} />
            <Pager first={rows.length ? from + 1 : 0} last={Math.min(from + per, rows.length)} total={rows.length} noun="uploads"
                page={current} pages={pages} onPrev={() => setPage(current - 1)} onNext={() => setPage(current + 1)}
                perPage={per} perOptions={[10, 25, 50]} onPerPage={(n) => { setPer(n); setPage(1); }} />
        </WidgetCard>
    );
}

// --- Serving ---------------------------------------------------------------------------

function ServingTab({ serving }: { serving?: LaunchServing | null }) {
    const groups = serving?.serving_groups ?? [];
    const vectors = serving?.intended_vectors ?? [];
    return (
        <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <WidgetCard title="Serving Groups" count={groups.length} isEmpty={!groups.length}>
                <GridTable minWidth={820}
                    columns={[
                        { label: "ID", track: "90px", tone: "id" }, { label: "Name", track: "minmax(240px, 2fr)", tone: "strong" }, { label: "Is Active", track: "90px" },
                        { label: "Constituents Count", track: "150px", align: "right" }, { label: "Vectors", track: "1fr" }, { label: "Created By", track: "1fr" },
                    ]}
                    rows={groups.map((g) => [text(g.id), text(g.name), <YesNo value={g.is_active} />, num(g.constituents_count), tags(g.vectors), text(g.created_by)])} />
            </WidgetCard>
            <WidgetCard title="Intended Vectors" count={vectors.length} isEmpty={!vectors.length}>
                <div className={styles.tagRow}>{vectors.map((v, i) => <Tag key={v.id ?? i}>{show(v.name)}</Tag>)}</div>
            </WidgetCard>
        </section>
    );
}

// --- Reporting -------------------------------------------------------------------------

const CAP_ATTR_COLUMNS: GridColumn[] = [
    { label: "Launch Attribute", tone: "mono" }, { label: "Conversion Payment", align: "right" }, { label: "Reversion Payment", align: "right" },
    { label: "Min Days To Charge", align: "right" },
];

function ReportingTab({ reporting }: { reporting?: LaunchReporting | null }) {
    const links = reporting?.report_links ?? [];
    const periods = reporting?.time_periods ?? [];
    const metrics = reporting?.performance_metrics ?? [];
    const caps = reporting?.caps ?? [];
    const sf = reporting?.salesforce_opportunity_ids ?? [];
    return (
        <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <WidgetCard title="Report Links" count={links.length} isEmpty={!links.length}>
                <GridTable columns={[{ label: "Label", tone: "strong" }, { label: "URL", track: "2fr" }]} rows={links.map((l) => [text(l.label), ext(l.report_url)])} />
            </WidgetCard>
            <WidgetCard title="Time Periods" count={periods.length} isEmpty={!periods.length}>
                <GridTable columns={[{ label: "Label", tone: "strong" }, { label: "Start Date" }, { label: "End Date" }]}
                    rows={periods.map((p) => [text(p.label), text(p.start_date), text(p.end_date)])} />
            </WidgetCard>
            <WidgetCard title="Performance Metrics" count={metrics.length} isEmpty={!metrics.length}>
                <GridTable minWidth={1000}
                    columns={[
                        { label: "Label", tone: "strong" }, { label: "Nickname" }, { label: "Launch Attribute", tone: "mono" }, { label: "Value", align: "right" },
                        { label: "Metric" }, { label: "Target", align: "right" }, { label: "Show On Dashboard" }, { label: "Is Primary" }, { label: "Description", track: "1.4fr", tone: "muted" },
                    ]}
                    rows={metrics.map((m) => [
                        text(m.label), text(m.nickname), text(m.launch_attribute), text(m.value), text(m.metric), text(m.target),
                        <YesNo value={m.show_on_dashboard} />, <YesNo value={m.is_primary} />, text(m.description),
                    ])} />
            </WidgetCard>
            <WidgetCard title="Caps" count={caps.length} isEmpty={!caps.length}>
                {caps.map((cap, i) => (
                    <div key={cap.sequential_number ?? i} className={styles.rowWrap}>
                        <GridTable minWidth={1000}
                            columns={[
                                { label: "#", track: "44px", tone: "id" }, { label: "Name", track: "1fr", tone: "strong" }, { label: "Start Date", track: "110px" },
                                { label: "End Date", track: "110px" }, { label: "Has Total Amount", track: "130px" }, { label: "Total Amount", track: "120px", align: "right" },
                                { label: "Budget", track: "1fr" }, { label: "Description", track: "1.4fr", tone: "muted" },
                            ]}
                            rows={[[
                                text(cap.sequential_number), text(cap.name), text(cap.start_date), text(cap.end_date), <YesNo value={cap.has_total_amount} />,
                                text(cap.total_amount), text(cap.budget?.name), text(cap.description),
                            ]]} />
                        {!!cap.attributes?.length && (
                            <div className={`${styles.panel} ${styles.panelFlush}`}>
                                <div>
                                    <div className={`${styles.miniLabel} ${styles.nestedLabel}`}>Attributes · {cap.attributes.length}</div>
                                    <div className={styles.nested}>
                                        <GridTable columns={CAP_ATTR_COLUMNS}
                                            rows={cap.attributes.map((a) => [text(a.launch_attribute), text(a.conversion_payment), text(a.reversion_payment), text(a.min_days_after_conversion_to_charge)])} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </WidgetCard>
            <WidgetCard title="Salesforce Opportunity IDs" count={sf.length} isEmpty={!sf.length}>
                <div className={styles.tagRow}>{sf.map((id, i) => <Tag key={i} mono>{id}</Tag>)}</div>
            </WidgetCard>
        </section>
    );
}

// --- Events ----------------------------------------------------------------------------

function EventsTab({ events }: { events?: LaunchEvent[] | null }) {
    const rows = events ?? [];
    return (
        <WidgetCard title="Events" count={rows.length}>
            {rows.length ? (
                <GridTable columns={[{ label: "Date", track: "180px" }, { label: "Time", track: "120px" }, { label: "Description", track: "2fr" }, { label: "Is Active", track: "100px" }]}
                    rows={rows.map((e) => [text(e.date), text(e.time), text(e.description), <YesNo value={e.is_active} />])} />
            ) : (
                <EmptyState title="No events recorded for this launch" body="Events from the Old Platform warehouse would appear here, newest first." />
            )}
        </WidgetCard>
    );
}

export const TABS: { key: string; label: string; render: (detail: LaunchDetail) => ReactNode }[] = [
    { key: "main", label: "Main", render: (d) => <MainTab main={d.main} /> },
    { key: "options", label: "Options", render: (d) => <OptionsTab options={d.options} /> },
    { key: "publishers", label: "Publishers", render: (d) => <PublishersTab publishers={d.publishers} /> },
    { key: "data_spec", label: "Data Spec", render: (d) => <DataSpecTab spec={d.data_spec} /> },
    { key: "upload_history", label: "Upload History", render: (d) => <UploadHistoryTab history={d.upload_history} /> },
    { key: "serving", label: "Serving", render: (d) => <ServingTab serving={d.serving} /> },
    { key: "reporting", label: "Reporting", render: (d) => <ReportingTab reporting={d.reporting} /> },
    { key: "events", label: "Events", render: (d) => <EventsTab events={d.events} /> },
];
